// v0.4.91 — Banner split: setup vs reconciliation failures.
//
// Operator sign-off 2026-06-20 (choices 1d + 2c):
//   1d. Multi-error scan ambiguity. When N>1 reconciliation
//       failures fire, the banner now exposes the count at three
//       scan-speeds: (a) title suffix "· N issues", (b) per-row
//       "Issue X of N" chip, (c) full-width rose divider between
//       rows so two failures never blur into one visual block.
//   2c. The IT LIPE cadence wizard (`vp1_period_set`) is a
//       one-time client configuration that happens to gate
//       filing — it is NOT a reconciliation failure. The rose
//       "refusing to file" banner conflated the two categories.
//       Setup failures now render in their own amber
//       "Client setup needed" card ABOVE the rose error banner,
//       so the rose banner is reliably about data errors only.
//
// v0.4.83/.84/.85/.89 behaviours preserved unchanged inside the
// rose error banner: plain-English headline via ruleCopy, per-
// offender Inspect/Flag-drift rows, InfoCircle-collapsed
// technical details.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { patchCountryReturnBox, updateClient } from "../../lib/api";
import { OffendersTable as Offenders } from "./OffendersTable";
import { getRuleCopy } from "../../lib/ruleCopy";
import { InfoCircle } from "../InfoCircle";

// v0.4.91 (2c) — Rule names that represent one-time client setup,
// not a data-quality failure. Routed to the amber setup card.
const SETUP_RULES = new Set(["vp1_period_set"]);

export const jumpToBox = (bid) => {
  const el = document.querySelector(`[data-testid="box-${bid}"]`);
  // A2-581 lead 26: a box hidden by the copy-only filter or in another
  // module tab: the page shows it, then scrolls (CountryReturn revealBox).
  if (!el) { window.dispatchEvent(new CustomEvent("cr-reveal-box", { detail: { boxId: bid } })); return; }
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-amber-400");
    setTimeout(
      () => el.classList.remove("ring-2", "ring-amber-400"),
      3000,
    );
  }
};

const monthFromPeriod = (period_start) => {
  if (!period_start) return null;
  const m = /^\d{4}-(\d{2})/.exec(period_start);
  return m ? parseInt(m[1], 10) : null;
};

const quarterFromPeriod = (period_start) => {
  const mo = monthFromPeriod(period_start);
  return mo ? Math.ceil(mo / 3) : null;
};

const MONTH_NAMES_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const headlineFor = (failure) => {
  const c = getRuleCopy(failure?.name, failure?.error);
  return { headline: c.headline, fix: c.fix };
};

const Vp1CadenceWizard = ({ calcId, country, clientId, periodStart, periodEnd, onResolved }) => {
  const { t } = useTranslation();
  const [pick, setPick] = useState(null);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const mo = monthFromPeriod(periodStart);
  const qt = quarterFromPeriod(periodStart);
  const monthLabel = mo ? MONTH_NAMES_EN[mo - 1] : t("ui.country_return.setup.this_month", { defaultValue: "this month" });
  const quarterLabel = qt ? `Q${qt}` : t("ui.country_return.setup.this_quarter", { defaultValue: "this quarter" });
  const choices = [
    { key: "MONTHLY", boxId: "VP1_month", value: mo,
      title: t("ui.country_return.setup.monthly_title", { defaultValue: "Monthly filer" }),
      sub: `Sets VP1_month = ${mo ?? "?"} (${monthLabel})` },
    { key: "QUARTERLY_ORD", boxId: "VP1_quarter", value: qt,
      title: t("ui.country_return.setup.quarterly_ord_title", { defaultValue: "Quarterly filer — ordinary" }),
      sub: `Sets VP1_quarter = ${qt ?? "?"} (${quarterLabel})` },
    { key: "QUARTERLY_SPEC", boxId: "VP1_quarter", value: 5,
      title: t("ui.country_return.setup.quarterly_spec_title", { defaultValue: "Quarterly filer — special (Art. 7 DPR 542/99)" }),
      sub: t("ui.country_return.setup.quarterly_spec_sub", { defaultValue: "Sets VP1_quarter = 5 (agriculture / motor fuel / autotrasporti)" }) },
  ];
  const apply = async () => {
    const c = choices.find((x) => x.key === pick);
    if (!c) return;
    setBusy(true); setErr(null);
    try {
      await patchCountryReturnBox(calcId, country, c.boxId, {
        value: String(c.value),
        reason: `Cadence wizard: ${pick} filer for period ${periodStart} → ${periodEnd} on ${country}.`,
      });
      if (remember && clientId) {
        const cad = pick === "MONTHLY" ? "MONTHLY" : "QUARTERLY";
        await updateClient(clientId, { local_return_frequency: { [country]: cad } });
      }
      onResolved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail?.message || e?.message || "Failed to apply cadence");
      setBusy(false);
    }
  };
  return (
    <div data-testid="vp1-cadence-wizard" className="rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-slate-900 p-3 mt-3 space-y-3">
      <div className="text-xs font-semibold text-slate-900 dark:text-slate-50">
        {t("ui.country_return.setup.wizard_title", { defaultValue: "Pick your filing cadence — we'll fill the right box for you." })}
      </div>
      <div className="grid grid-cols-1 gap-2">
        {choices.map((c) => {
          const active = pick === c.key;
          return (
            <button
              key={c.key} type="button"
              data-testid={`vp1-cadence-${c.key.toLowerCase()}`}
              onClick={() => setPick(c.key)} disabled={busy || c.value == null}
              className={`text-left rounded-md border px-3 py-2 transition-colors ${
                active
                  ? "border-teal-500 bg-teal-50 dark:bg-teal-900/40 ring-2 ring-teal-200 dark:ring-teal-800"
                  : "border-slate-300 dark:border-slate-700 hover:border-teal-400 dark:hover:border-teal-500 bg-white dark:bg-slate-900"
              } ${busy || c.value == null ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <div className="text-sm font-medium text-slate-900 dark:text-slate-50">{c.title}</div>
              <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{c.sub}</div>
            </button>
          );
        })}
      </div>
      {clientId && (
        <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
          <input type="checkbox" data-testid="vp1-cadence-remember"
            checked={remember} onChange={(e) => setRemember(e.target.checked)} disabled={busy} />
          {t("ui.country_return.setup.remember", { country, defaultValue: `Remember this cadence on the client's ${country} registration (auto-resolves all future ${country} returns).` })}
        </label>
      )}
      {err && <div data-testid="vp1-cadence-error" className="text-xs text-rose-700 dark:text-rose-300">{err}</div>}
      <div className="flex gap-2 items-center">
        <button type="button" data-testid="vp1-cadence-apply" onClick={apply} disabled={!pick || busy}
          className={`text-sm font-semibold px-4 py-2 rounded-lg ${
            !pick || busy
              ? "bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed"
              : "bg-teal-600 hover:bg-teal-700 text-white"
          }`}>
          {busy ? t("ui.country_return.setup.applying", { defaultValue: "Applying…" }) : t("ui.country_return.setup.apply", { defaultValue: "Apply & resolve" })}
        </button>
        <span className="text-[11px] text-slate-500 dark:text-slate-400">
          {t("ui.country_return.setup.reloads", { defaultValue: "Reloads the return once the box is set." })}
        </span>
      </div>
    </div>
  );
};

import {
  countrySupportsManualOverride, manualOverrideBoxFor,
} from "../../lib/manualOverrideConfig";
import { useReviewCardHref } from "./reviewCardLink";   // A2-581 lead 6

// v0.5.0c+6 — Rule names whose ONLY genuine resolution paths
// live per-offender (Inspect → Supersede / Re-upload / Flag
// drift). For these, NO "Open correction box" CTA can ever be
// correct — filling any box is a typed-in value that does not
// modify `ctx.rate_split`, so the unknown-rate guard would
// still refuse to file. The defensive set below means a future
// agent who accidentally re-adds `requires_input` to one of
// these checks STILL gets the right UX — the misleading CTA
// stays suppressed.
const UNKNOWN_RATE_RULES = new Set([
  "rate_split_keys_match_known_categories",
  "fr_rate_split_keys_match_known_categories",
  "es_rate_split_keys_match_known_categories",
  "uk_rate_split_keys_match_known_categories",
  "it_rate_split_keys_match_known_categories",
]);

const ActionRow = ({ failure, country }) => {
  const { t } = useTranslation();
  const cardHref = useReviewCardHref("VAT_RATE_MISMATCH");
  const hasOffenders = (failure?.offenders || []).length > 0;
  const isUnknownRate = UNKNOWN_RATE_RULES.has(failure?.name);
  const primaryBox = (failure.requires_input || [])[0];

  // v0.5.0d Q5 — country-aware copy for unknown-rate failures.
  // ONE recommended action, alternatives secondary. Mirrors the
  // "still-blocked" priority pointer that OffendersTable renders
  // once every offender is drift-flagged. Drops the older
  // "Inspect → Supersede / Re-upload corrected CSV / Flag drift"
  // unsorted-list copy (it contradicted the Q5 lead).
  if (isUnknownRate && hasOffenders) {
    const cc = (country || "").toUpperCase();
    const hasManual = countrySupportsManualOverride(cc);
    const boxId = manualOverrideBoxFor(cc);
    const n = (failure.offenders || []).reduce((s, o) => s + (Number(o.txn_count) || 0), 0);
    return (
      <div
        data-testid={`actions-${failure.name}`}
        className="mt-2 text-[11px] leading-snug text-rose-700 dark:text-rose-300"
      >
        {/* A2-523 (operator 2026-10-25: "this step is confusing, need to give
            clearer instructions ... where's the option to change the VAT
            rate?"). The plain steps first, with the way to the rate pick;
            the older line below stays for the other ways out. */}
        <div data-testid="unknown-rate-what-to-do" className="mb-2 rounded-md border border-rose-300 dark:border-rose-800 bg-white/60 dark:bg-slate-900/40 p-2.5 space-y-1.5 text-slate-800 dark:text-slate-100">
          <div className="font-bold">{n ? t("ui.country_return.unknown_rate.what", { n, defaultValue: `What to do: ${n} sale(s) have VAT that is not one of this form's rates (the rate is VAT ÷ Net), so the return has no box for them.` })
            : t("ui.country_return.unknown_rate.what_no_count", { defaultValue: "What to do: these sales have VAT that is not one of this form's rates (the rate is VAT ÷ Net), so the return has no box for them." })}</div>
          <div>{t("ui.country_return.unknown_rate.step1", { defaultValue: "1. If the VAT in your file is wrong (the usual case): pick the right rate for these rows. The VAT is then worked out from that rate, replacing the file's figure. Then re-run the calculation." })}</div>
          <Link to={cardHref} data-testid="unknown-rate-pick-rate"
            className="inline-block text-xs font-bold px-3 py-1.5 rounded-md bg-rose-700 hover:bg-rose-600 text-white">
            {t("ui.country_return.unknown_rate.pick", { defaultValue: "Pick the right rate on Step 4 →" })}
          </Link>
          <div>{t("ui.country_return.unknown_rate.step2", { defaultValue: "2. If the invoices really charged that VAT: correct those rows in your file and upload it again." })}</div>
        </div>
        →{" "}
        {hasManual ? (
          <>{/* i18n-ignore: pinned by test_v0501c6 */}
            To file today: open the flagged row(s) below and either{" "}
            (a) <span className="font-bold">Assign to {cc} manual-other-rates box (Kz {boxId})</span> if the rate is real,
            or (b) <span className="font-bold">Replace this calculation</span> with one that drops the rows if the source data is wrong.{/* i18n-ignore: wording pinned by test_v0501c6; translate with that test */}
            {" "}Reporting to the rate-table maintainer alone does not unblock filing.
          </>
        ) : (
          <>{/* i18n-ignore: pinned by test_v0501c6 */}
            To file today: open the flagged row(s) below and{" "}
            <span className="font-bold">Replace this calculation</span> with one that drops them.{/* i18n-ignore: pinned by test_v0501c6 */}
            {" "}{cc || "This country"} has no manual-other-rates box on this form — fixing the source data is the only way to file.
            {" "}Reporting to the rate-table maintainer alone does not unblock filing.
          </>
        )}
      </div>
    );
  }

  if (!primaryBox) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid={`actions-${failure.name}`}>
      <button
        type="button"
        data-testid={`action-open-correction-${failure.name}`}
        onClick={() => jumpToBox(primaryBox)}
        className="text-xs font-semibold px-3 py-1.5 rounded-md bg-rose-700 hover:bg-rose-600 text-white"
      >
        {t("ui.country_return.open_correction_box", { box: primaryBox, defaultValue: `Open correction box ${primaryBox} →` })}
      </button>
    </div>
  );
};

// v0.4.91 (2c) — Amber "one-time configuration" card. Renders
// above the rose error banner. Currently only `vp1_period_set`
// (IT LIPE cadence wizard) lives here, but the same surface
// will host future client-setup gates (e.g. ES Modelo 303
// special-regime registration) without churning the rose banner.
const ClientSetupCard = ({
  failure, calcId, country, clientId, clientCode,
  periodStart, periodEnd, savedCadence, onResolved,
}) => { const { t } = useTranslation(); return (
  <div
    data-testid={`client-setup-${failure.name}`}
    className="bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-600 rounded-lg p-4 mb-4"
  >
    <div className="flex items-center gap-2 mb-1">
      <div
        data-testid="client-setup-title"
        className="text-amber-900 dark:text-amber-100 font-semibold text-sm"
      >
        ⚙ {t("ui.country_return.setup.title", { defaultValue: "Client setup needed" })}
      </div>
      <span
        className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-amber-200 dark:bg-amber-900 text-amber-900 dark:text-amber-100 border border-amber-300 dark:border-amber-700"
        data-testid="client-setup-badge"
      >
        {t("ui.country_return.setup.badge", { defaultValue: "One-time" })}
      </span>
    </div>
    <div
      className="text-[11px] text-amber-800/90 dark:text-amber-200/90 mb-3 leading-snug"
    >
      {clientCode && <>{t("ui.country_return.setup.client_label", { defaultValue: "Client:" })} <span className="font-bold">{clientCode}</span> · </>}
      {country && <>{t("ui.country_return.setup.country_label", { defaultValue: "Country:" })} <span className="font-bold">{country}</span> · </>}
      {periodStart && periodEnd && (
        <>{t("ui.country_return.setup.period_label", { defaultValue: "Period:" })} <span className="font-bold">{periodStart} → {periodEnd}</span> · </>
      )}
      {t("ui.country_return.setup.frequency_label", { defaultValue: "Filing frequency on file:" })} <span className="font-bold">{savedCadence || t("ui.country_return.setup.not_set", { defaultValue: "not set" })}</span>
    </div>
    <div className="text-sm text-amber-900 dark:text-amber-50 font-semibold leading-snug mb-1">{/* i18n-ignore: pinned by test_v0491 */}
      This isn&apos;t a data error — {country || "the country"} needs you to set the client&apos;s filing frequency once.
    </div>
    <div className="text-xs text-amber-800 dark:text-amber-200 leading-snug">
      {headlineFor(failure).headline}
    </div>
    <Vp1CadenceWizard
      calcId={calcId} country={country} clientId={clientId}
      periodStart={periodStart} periodEnd={periodEnd}
      onResolved={onResolved}
    />
  </div>
); };

// v0.4.91 (1d) — Numbered chip "Issue X of N" renders only when
// there's more than one error failure so single-error banners
// don't gain meaningless "Issue 1 of 1" noise.
const IssueChip = ({ idx, total }) => {
  if (total <= 1) return null;
  return (
      <span data-testid={`issue-chip-${idx + 1}`}
        className="inline-flex items-center text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-rose-200 dark:bg-rose-900 text-rose-900 dark:text-rose-100 border border-rose-300 dark:border-rose-700 mb-1"
    >
      Issue {idx + 1} of {total}
    </span>
  );
};

// A2-554 (operator 2026-10-27: "Should be collapsed by default and then open
// when need to fix it. Otherwise - confusing."): one line per issue — what it
// is and how much it touches — with its rows and fixes folded away.
const issueSummary = (f, t) => {
  const offs = f?.offenders || [];
  if (!offs.length) return null;
  const sales = offs.reduce((s, o) => s + (Number(o.txn_count) || 0), 0);
  return t("ui.country_return.issue_summary", {
    lines: offs.length, sales,
    defaultValue: `${offs.length} line(s) · ${sales} sale(s)`,
  });
};

const ErrorFailureRow = ({
  f, idx, total, calcId, country, periodStart, periodEnd,
}) => {
  const { t } = useTranslation();
  const { headline, fix } = headlineFor(f);
  const [open, setOpen] = useState(false);
  const summary = issueSummary(f, t);
  const detailsId = `failure-details-${f.name}`;
  return (
    <li
      data-testid={`reconciliation-failure-${f.name}`}
      className="border-l-2 border-rose-400 pl-3"
    >
      <IssueChip idx={idx} total={total} />
      <div className="flex items-start gap-1.5">
        <div className="text-sm sm:text-[15px] text-rose-900 dark:text-rose-50 font-bold leading-snug flex-1">
          {headline}
        </div>
        {(fix || f.description || f.error) && (
          <InfoCircle testid={`failure-info-${f.name}`} align="right">
            {fix && (
              <div className="text-rose-800 dark:text-rose-100 mb-2">
                {fix}
              </div>
            )}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-2 mt-2">
              <div className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide font-semibold mb-1">
                {t("ui.country_return.tech_details", { defaultValue: "Technical details (for audit / support)" })}
              </div>
              <div className="leading-snug text-slate-600 dark:text-slate-300">
                Rule: <span className="font-semibold">{f.name}</span>
              </div>
              {f.description && (
                <div className="leading-snug text-slate-600 dark:text-slate-300 mt-1">
                  {f.description}
                </div>
              )}
              {f.error && (
                <div className="leading-snug text-rose-700 dark:text-rose-300 mt-1">
                  {t("ui.country_return.engine_error", { error: f.error, defaultValue: `Engine: ${f.error}` })}
                </div>
              )}
            </div>
          </InfoCircle>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-1">
        {summary && (
          <span data-testid={`failure-summary-${f.name}`}
            className="text-xs tabular-nums text-rose-800 dark:text-rose-200">{summary}</span>
        )}
        <button
          type="button"
          data-testid={`failure-toggle-${f.name}`}
          aria-expanded={open}
          aria-controls={detailsId}
          onClick={() => setOpen((o) => !o)}
          className="text-xs font-semibold px-2.5 py-1 rounded-md border border-rose-400 dark:border-rose-700 text-rose-800 dark:text-rose-100 hover:bg-rose-200/60 dark:hover:bg-rose-900/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-rose-500"
        >
          {open
            ? t("ui.country_return.hide_fix", { defaultValue: "Hide details" })
            : t("ui.country_return.show_fix", { defaultValue: "Show details and fix →" })}
        </button>
      </div>
      {open && (
        <div id={detailsId} data-testid={detailsId}>
          <Offenders
            failure={f} calcId={calcId} country={country}
            periodStart={periodStart} periodEnd={periodEnd}
          />
          <ActionRow failure={f} country={country} />
        </div>
      )}
    </li>
  );
};

const ErrorBanner = ({
  failures, calcId, country, clientCode,
  periodStart, periodEnd, savedCadence,
}) => {
  const { t } = useTranslation();
  const total = failures.length;
  return (
    <div
      data-testid="reconciliation-failures"
      className="bg-rose-100 dark:bg-rose-950/60 border-2 border-rose-500 dark:border-rose-600 border-l-8 border-l-rose-600 dark:border-l-rose-500 rounded-lg p-5 mb-6 shadow-lg shadow-rose-500/20 dark:shadow-rose-900/40"
    >
      <div className="flex items-baseline gap-2 mb-2">
        <div className="text-rose-900 dark:text-rose-50 font-bold text-base sm:text-lg flex items-center gap-2">
          <span aria-hidden="true">⚠</span>
          <span>{t("ui.country_return.banner.cannot_file", { defaultValue: "Cannot file this return" })}</span>
        </div>
        {total > 1 && (
          <span
            data-testid="reconciliation-failures-count"
            className="text-xs font-semibold text-rose-800 dark:text-rose-200 bg-rose-200 dark:bg-rose-900 px-2 py-0.5 rounded-full"
          >
            {total} issues
          </span>
        )}
      </div>
      {(clientCode || periodStart) && (
        <div
          data-testid="reconciliation-failures-context"
          className="text-[11px] text-rose-700/90 dark:text-rose-300/90 mb-3 leading-snug"
        >
          {clientCode && <>{t("ui.country_return.setup.client_label", { defaultValue: "Client:" })} <span className="font-bold">{clientCode}</span> · </>}
          {country && <>{t("ui.country_return.setup.country_label", { defaultValue: "Country:" })} <span className="font-bold">{country}</span> · </>}
          {periodStart && periodEnd && (
            <>{t("ui.country_return.setup.period_label", { defaultValue: "Period:" })} <span className="font-bold">{periodStart} → {periodEnd}</span> · </>
          )}
          {t("ui.country_return.setup.frequency_label", { defaultValue: "Filing frequency on file:" })} <span className="font-bold">{savedCadence || t("ui.country_return.setup.not_set", { defaultValue: "not set" })}</span>
        </div>
      )}
      {/* v0.4.91 (1d) — full-width rose divider between issues
          so two failures don't blur into one block. `divide-y`
          with rose tone gives a clear horizontal break that a
          thin left-border alone could not. */}
      <ul
        data-testid="reconciliation-failures-list"
        className="divide-y divide-rose-200 dark:divide-rose-800"
      >
        {failures.map((f, i) => (
          <div key={f.name + i} className={i === 0 ? "pb-3" : "py-3 last:pb-0"}>
            <ErrorFailureRow
              f={f} idx={i} total={total}
              calcId={calcId} country={country}
              periodStart={periodStart} periodEnd={periodEnd}
            />
          </div>
        ))}
      </ul>
    </div>
  );
};

export const ActionRequiredBanner = ({
  failures, calcId, country, clientId, clientCode,
  periodStart, periodEnd, savedCadence, onResolved,
}) => {
  const setupFails = failures.filter((f) => SETUP_RULES.has(f.name));
  const errorFails = failures.filter((f) => !SETUP_RULES.has(f.name));
  return (
    <div data-testid="action-required-region">
      {setupFails.map((f) => (
        <ClientSetupCard
          key={f.name}
          failure={f} calcId={calcId} country={country}
          clientId={clientId} clientCode={clientCode}
          periodStart={periodStart} periodEnd={periodEnd}
          savedCadence={savedCadence} onResolved={onResolved}
        />
      ))}
      {errorFails.length > 0 && (
        <ErrorBanner
          failures={errorFails} calcId={calcId} country={country}
          clientCode={clientCode} periodStart={periodStart}
          periodEnd={periodEnd} savedCadence={savedCadence}
        />
      )}
    </div>
  );
};
